// منطق ساخت و اعتبارسنجی خط مصرف کارگاه (AM-033)
// بدون وابستگی به React جهت تست‌پذیری مستقل و تضمین ناورداها
import type {
  LotConversionAdjustmentInput,
  UnifiedConsumptionLineInput,
} from "@/api/unifiedConsumption";

const DECIMAL_RE = /^\d{1,10}(\.\d{1,4})?$/; // مثبت، حداکثر ۴ رقم اعشار (هم‌خوان با DecimalField)
const INTEGER_RE = /^\d{1,10}$/;

export interface LineFormState {
  bomItemId: string;
  lot: { id: string; display: string } | null;
  consumptionQuantity: string;
  scrapEnabled: boolean;
  scrapQuantity: string;
  scrapReasonCode: string;
  /** مجوز واقعی کاربر برای تعدیل ضریب (بر اساس inventory.manage) */
  adjustAllowed: boolean;
  adjustEnabled: boolean;
  adjustedNumerator: string;
  adjustedDenominator: string;
  adjustReason: string;
}

export type BuildLineResult =
  | { ok: true; line: UnifiedConsumptionLineInput }
  | { ok: false; error: string };

const fail = (error: string): BuildLineResult => ({ ok: false, error });

export function buildConsumptionLine(s: LineFormState): BuildLineResult {
  if (!s.lot) return fail("لطفاً شناسه پالت را انتخاب کنید.");

  const consumption = s.consumptionQuantity.trim();
  if (!DECIMAL_RE.test(consumption)) {
    return fail("مقدار مصرف را به‌صورت عدد معتبر (حداکثر ۴ رقم اعشار) وارد کنید.");
  }

  let scrapQuantity = "0";
  let scrapReasonCode: string | undefined;
  if (s.scrapEnabled) {
    const raw = s.scrapQuantity.trim() || "0";
    if (!DECIMAL_RE.test(raw)) {
      return fail("مقدار ضایعات را به‌صورت عدد معتبر (حداکثر ۴ رقم اعشار) وارد کنید.");
    }
    scrapQuantity = raw;
    if (Number(raw) > 0) {
      if (!s.scrapReasonCode) return fail("علت ضایعات الزامی است.");
      scrapReasonCode = s.scrapReasonCode;
    }
  }

  // قاعده‌ی مصوب سیدمحمد: رکورد کاملاً صفر ممنوع؛ مصرف صفر فقط وقتی ضایعات مثبت است.
  if (!(Number(consumption) > 0 || Number(scrapQuantity) > 0)) {
    return fail("حداقل یکی از مقدار مصرف یا ضایعات باید بزرگ‌تر از صفر باشد.");
  }

  let adjustment: LotConversionAdjustmentInput | undefined;
  if (s.adjustAllowed && s.adjustEnabled) {
    const num = s.adjustedNumerator.trim();
    const den = s.adjustedDenominator.trim();
    if (!INTEGER_RE.test(num) || Number(num) <= 0) {
      return fail("صورت ضریب تبدیل باید عدد صحیح مثبت باشد.");
    }
    if (!DECIMAL_RE.test(den) || Number(den) <= 0) {
      return fail("مخرج ضریب تبدیل باید عدد مثبت (حداکثر ۴ رقم اعشار) باشد.");
    }
    // قاعده‌ی مصوب سیدمحمد: دلیل تغییر ضریب الزامی است (ممیزی).
    const reason = s.adjustReason.trim();
    if (!reason) return fail("دلیل تغییر ضریب تبدیل الزامی است.");
    adjustment = {
      adjusted_numerator: Number(num),
      adjusted_denominator: den,
      reason,
    };
  }

  return {
    ok: true,
    line: {
      bom_item: s.bomItemId,
      lot: s.lot.id,
      consumption_quantity: consumption,
      scrap_quantity: scrapQuantity,
      scrap_reason_code: scrapReasonCode,
      adjust_conversion_factor: adjustment, // کلید همیشه وجود دارد (undefined = ارسال‌نشدن)
    },
  };
}
